import type { OfferLetterTemplate } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Edit, Eye, FileText, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { OfferTemplateIframe } from "@/components/offer-template-iframe";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useQueryParams } from "@/hooks/use-query-params";
import { useToast } from "@/hooks/use-toast";
import { renderOfferTemplatePreview } from "@/lib/offer-template-utils";
import { orpc } from "@/lib/rpc";

const DEFAULT_HTML_TEMPLATE = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Oferta de Emprego - Comp</title>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; line-height: 1.6; color: #262626; background: #F5F3F0; }
        .container { max-width: 750px; margin: 0 auto; padding: 60px 24px; }
        .header { text-align: center; margin-bottom: 64px; }
        .logo { font-size: 48px; font-weight: 700; color: #F3344A; letter-spacing: -0.04em; margin-bottom: 12px; }
        .tagline { font-size: 15px; color: #737373; }
        .greeting { font-size: 32px; font-weight: 600; margin-bottom: 32px; color: #171717; }
        .intro { font-size: 17px; line-height: 1.7; margin-bottom: 48px; color: #404040; }
        .about-comp { background: #FFF; border: 1px solid #E5E5E5; border-left: 4px solid #F3344A; padding: 32px; border-radius: 12px; margin-bottom: 48px; }
        .offer-details { background: #FFF; border: 1px solid #E5E5E5; border-radius: 12px; padding: 32px; margin-bottom: 48px; }
        .offer-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; margin-top: 24px; }
        .offer-item { padding: 24px; border: 1px solid #E5E5E5; border-radius: 8px; background: #FAFAFA; }
        .offer-label { font-size: 13px; text-transform: uppercase; color: #737373; margin-bottom: 8px; font-weight: 500; }
        .offer-value { font-size: 28px; font-weight: 700; color: #171717; }
        .cta-section { background: #F3344A; color: white; padding: 40px 36px; border-radius: 12px; text-align: center; margin: 56px 0; }
        @media (max-width: 640px) { .offer-grid { grid-template-columns: 1fr; } }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">Comp</div>
            <div class="tagline">Building the future of work</div>
        </div>
        <div class="greeting">Olá, {{candidateName}}! 👋</div>
        <div class="intro">
            <p>É com <strong>enorme satisfação</strong> que estendemos a você uma oferta para se juntar ao time da Comp como <strong>{{jobTitle}}</strong>.</p>
        </div>
        <div class="about-comp">
            <h2>🚀 Sobre a Comp</h2>
            <p>A Comp está construindo a <strong>infraestrutura de compensation</strong> para o futuro do trabalho.</p>
        </div>
        <div class="offer-details">
            <h2 style="margin-bottom: 24px;">💰 Sua Oferta</h2>
            <div class="offer-grid">
                <div class="offer-item">
                    <div class="offer-label">Salário Base</div>
                    <div class="offer-value">{{salaryBase}}</div>
                </div>
                <div class="offer-item">
                    <div class="offer-label">Equity</div>
                    <div class="offer-value">{{equityShares}}</div>
                </div>
                <div class="offer-item">
                    <div class="offer-label">Bônus</div>
                    <div class="offer-value">{{signingBonus}}</div>
                </div>
            </div>
        </div>
        <div class="cta-section">
            <div style="font-size: 32px; font-weight: 700; margin-bottom: 20px;">Bora querer? 🚀</div>
            <p>Vamos construir isso juntos? 💜</p>
        </div>
    </div>
</body>
</html>`;

export default function OfferTemplates() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [previewTemplate, setPreviewTemplate] = useState<OfferLetterTemplate | null>(null);
  const { getParam, setParam } = useQueryParams();
  const selectedTab = getParam("tab") || "list";
  const setSelectedTab = (tab: string) => setParam("tab", tab);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<OfferLetterTemplate | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formHtmlContent, setFormHtmlContent] = useState("");
  const [formIsDefault, setFormIsDefault] = useState(false);

  const { data: templates, isLoading } = useQuery(orpc.offerTemplates.list.queryOptions());

  const createTemplateMutation = useMutation({
    ...orpc.offerTemplates.create.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offerTemplates.list.queryOptions().queryKey });
      toast({
        title: "Template criado",
        description: "O template foi criado com sucesso.",
      });
      closeEditDialog();
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível criar o template.",
        variant: "destructive",
      });
    },
  });

  const updateTemplateMutation = useMutation({
    ...orpc.offerTemplates.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offerTemplates.list.queryOptions().queryKey });
      toast({
        title: "Template atualizado",
        description: "O template foi atualizado com sucesso.",
      });
      closeEditDialog();
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível atualizar o template.",
        variant: "destructive",
      });
    },
  });

  const deleteTemplateMutation = useMutation({
    ...orpc.offerTemplates.delete.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offerTemplates.list.queryOptions().queryKey });
      toast({
        title: "Template deletado",
        description: "O template foi removido com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível deletar o template.",
        variant: "destructive",
      });
    },
  });

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setFormName("");
    setFormDescription("");
    setFormHtmlContent(DEFAULT_HTML_TEMPLATE);
    setFormIsDefault(false);
    setEditDialogOpen(true);
  };

  const openEditDialog = (template: OfferLetterTemplate) => {
    setEditingTemplate(template);
    setFormName(template.name);
    setFormDescription(template.description || "");
    setFormHtmlContent(template.htmlContent);
    setFormIsDefault(template.isDefault);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingTemplate(null);
    setFormName("");
    setFormDescription("");
    setFormHtmlContent("");
    setFormIsDefault(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formName.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, forneça um nome para o template.",
        variant: "destructive",
      });
      return;
    }

    if (!formHtmlContent.trim()) {
      toast({
        title: "HTML obrigatório",
        description: "Por favor, forneça o conteúdo HTML do template.",
        variant: "destructive",
      });
      return;
    }

    const data = {
      name: formName,
      description: formDescription || undefined,
      htmlContent: formHtmlContent,
      isDefault: formIsDefault,
    };

    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, ...data });
    } else {
      createTemplateMutation.mutate(data);
    }
  };

  const renderPreview = (template: OfferLetterTemplate) => {
    const previewHtml = renderOfferTemplatePreview(template.htmlContent);
    return <OfferTemplateIframe html={previewHtml} className="rounded-lg" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-2 p-6">
        {["a", "b", "c", "d", "e"].map((k) => (
          <div key={k} className="flex items-center gap-3 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--sand-divider))]" />
            <span className="h-4 flex-1 max-w-md rounded bg-[hsl(var(--sand-divider))]" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates de Carta de Oferta"
        description="Gerencie os templates de cartas de oferta de emprego"
      >
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Template
        </Button>
      </PageHeader>

      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="list">
            <FileText className="h-4 w-4 mr-2" />
            Lista de Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-4">
          {!templates || templates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-[hsl(var(--ink-muted))] mb-4" />
                <h3 className="text-lg font-semibold mb-2">Sem templates ainda</h3>
                <p className="text-sm text-[hsl(var(--ink-muted))] mb-4">
                  Cadastre o primeiro template de carta de oferta.
                </p>
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => (
                <Card key={template.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <CardTitle>{template.name}</CardTitle>
                          {template.isDefault && (
                            <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[hsl(var(--forest-pulse))]">
                              <CheckCircle2 className="h-3 w-3" />
                              Padrão
                            </span>
                          )}
                        </div>
                        {template.description && (
                          <CardDescription>{template.description}</CardDescription>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewTemplate(template)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Visualizar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(template)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (confirm("Tem certeza que deseja deletar este template?")) {
                              deleteTemplateMutation.mutate({ id: template.id });
                            }
                          }}
                          disabled={template.isDefault}
                          title={
                            template.isDefault ? "Não é possível deletar o template padrão" : ""
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Editar Template" : "Novo Template"}</DialogTitle>
            <DialogDescription>
              {editingTemplate
                ? "Atualize as informações do template de carta de oferta."
                : "Crie um novo template de carta de oferta. Use variáveis como {{candidateName}}, {{jobTitle}}, etc."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Nome do Template <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ex: Template Padrão Comp"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Breve descrição do template"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch id="isDefault" checked={formIsDefault} onCheckedChange={setFormIsDefault} />
              <Label htmlFor="isDefault" className="cursor-pointer">
                Definir como template padrão
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="htmlContent">
                Conteúdo HTML <span className="text-destructive">*</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Variáveis disponíveis:{" "}
                <code className="bg-muted px-1 py-0.5 rounded">{"{{candidateName}}"}</code>,{" "}
                <code className="bg-muted px-1 py-0.5 rounded">{"{{jobTitle}}"}</code>,{" "}
                <code className="bg-muted px-1 py-0.5 rounded">{"{{department}}"}</code>,{" "}
                <code className="bg-muted px-1 py-0.5 rounded">{"{{salaryBase}}"}</code>,{" "}
                {/* biome-ignore lint/security/noSecrets: template variable placeholders */}
                <code className="bg-muted px-1 py-0.5 rounded">{"{{equityShares}}"}</code>,{" "}
                <code className="bg-muted px-1 py-0.5 rounded">{"{{signingBonus}}"}</code>,{" "}
                {/* biome-ignore lint/security/noSecrets: template variable placeholders */}
                <code className="bg-muted px-1 py-0.5 rounded">{"{{performanceBonus}}"}</code>
              </p>
              <Textarea
                id="htmlContent"
                value={formHtmlContent}
                onChange={(e) => setFormHtmlContent(e.target.value)}
                className="font-mono text-xs min-h-[400px]"
                required
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeEditDialog}
                disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
              >
                {createTemplateMutation.isPending || updateTemplateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : editingTemplate ? (
                  "Atualizar"
                ) : (
                  "Criar Template"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
            <DialogDescription>Preview do template com dados de exemplo</DialogDescription>
          </DialogHeader>
          {previewTemplate && renderPreview(previewTemplate)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
